import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Layout } from './Layout';
import { LoginPage } from './LoginPage';
import { LogoutPage } from './LogoutPage';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { ProfilePage } from './ProfilePage';
import { NotFoundPage } from './NotFoundPage';
import { NoHomePage } from './NoHomePage';
import { ProtectedRoute } from '../auth/ProtectedRoute';

import { UsersListPage } from '../features/users/UsersListPage';
import { UserCreatePage } from '../features/users/UserCreatePage';
import { UserDetailPage } from '../features/users/UserDetailPage';
import { UserEditPage } from '../features/users/UserEditPage';
import { RolesListPage } from '../features/roles/RolesListPage';
import { RoleCreatePage } from '../features/roles/RoleCreatePage';
import { RoleEditPage } from '../features/roles/RoleEditPage';
import { SolicitudesListPage } from '../features/solicitudes/SolicitudesListPage';
import { KeycloakOrphansPage } from '../features/keycloak-orphans/KeycloakOrphansPage';
import { ClientesListPage } from '../features/clientes/ClientesListPage';
import { ClienteDetailPage } from '../features/clientes/ClienteDetailPage';
import { ClienteEditPage } from '../features/clientes/ClienteEditPage';
import { ServiciosListPage } from '../features/servicios/ServiciosListPage';
import { CobrosListPage } from '../features/cobros/CobrosListPage';
import { CobrosGenerarPage } from '../features/cobros/CobrosGenerarPage';
import { CobrosGeneracionHistorialPage } from '../features/cobros/CobrosGeneracionHistorialPage';
import { CorteMoraPage } from '../features/reportes/corte-mora/CorteMoraPage';
import { PagosListPage } from '../features/pagos/PagosListPage';
import { ExcepcionesListPage } from '../features/excepciones/ExcepcionesListPage';
import { VisitasListPage } from '../features/visitas/VisitasListPage';
import { CatalogosListPage } from '../features/catalogos/CatalogosListPage';
import { CatalogosCategoryPage } from '../features/catalogos/CatalogosCategoryPage';
import { CatalogCategoryCreatePage } from '../features/catalogos/CatalogCategoryCreatePage';
import { CatalogCategoryEditPage } from '../features/catalogos/CatalogCategoryEditPage';
import { CatalogoCreatePage } from '../features/catalogos/CatalogoCreatePage';
import { CatalogoDetailPage } from '../features/catalogos/CatalogoDetailPage';
import { CatalogoEditPage } from '../features/catalogos/CatalogoEditPage';
import { PlanesListPage } from '../features/planes/PlanesListPage';
import { PlanCreatePage } from '../features/planes/PlanCreatePage';
import { PlanDetailPage } from '../features/planes/PlanDetailPage';
import { PlanEditPage } from '../features/planes/PlanEditPage';
import { TiposServicioListPage } from '../features/tipos-servicio/TiposServicioListPage';
import { TipoServicioCreatePage } from '../features/tipos-servicio/TipoServicioCreatePage';
import { TipoServicioDetailPage } from '../features/tipos-servicio/TipoServicioDetailPage';
import { TipoServicioEditPage } from '../features/tipos-servicio/TipoServicioEditPage';
import { Permission } from '@aid/shared';
import { ComisionesPage } from '../features/comisiones/ComisionesPage';
import { MiCobroPage } from '../features/cobrador/MiCobroPage';
import { usePermissions } from '../providers';
import { LoadingScreen } from '../components/LoadingScreen';

/**
 * The admin sees the dashboard; an active cobrador goes directly to Mi Cobro.
 *
 * A linked user whose roles grant neither permission has no default home, so
 * they land on the empty `/no-home` screen with the header and sidebar still
 * available. `/no-access` is reserved for the `USER_NOT_LINKED` case: sending a
 * linked user there wrongly shows the "request access" page to someone who
 * already has an account.
 */
export function getHomeRouteDestination(
  permissions: readonly string[],
): '/mi-cobro' | '/no-home' | null {
  if (permissions.includes(Permission.DASHBOARD_READ)) return null;
  return permissions.includes(Permission.COBRADOR_READ) ? '/mi-cobro' : '/no-home';
}

export function HomeRoute() {
  const { permissions, isLoading } = usePermissions();
  if (isLoading) {
    return <LoadingScreen />;
  }

  const destination = getHomeRouteDestination(permissions);
  if (destination) {
    return <Navigate to={destination} replace />;
  }
  return <DashboardPage />;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/logout',
    element: <LogoutPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <HomeRoute /> },
      { path: 'no-home', element: <NoHomePage /> },
      {
        path: 'mi-cobro',
        element: (
          <ProtectedRoute permission={Permission.COBRADOR_READ}>
            <MiCobroPage />
          </ProtectedRoute>
        ),
      },
      { path: 'profile', element: <ProfilePage /> },
      {
        path: 'users',
        element: (
          <ProtectedRoute permission={Permission.USERS_READ}>
            <UsersListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'users/new',
        element: (
          <ProtectedRoute permission={Permission.USERS_CREATE}>
            <UserCreatePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'users/:id',
        element: (
          <ProtectedRoute permission={Permission.USERS_READ}>
            <UserDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'users/:id/edit',
        element: (
          <ProtectedRoute permission={Permission.USERS_UPDATE}>
            <UserEditPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'roles',
        element: (
          <ProtectedRoute permission={Permission.ROLES_READ}>
            <RolesListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'roles/new',
        element: (
          <ProtectedRoute permission={Permission.ROLES_CREATE}>
            <RoleCreatePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'roles/:id',
        element: (
          <ProtectedRoute permission={Permission.ROLES_READ}>
            <RoleEditPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'solicitudes',
        element: (
          <ProtectedRoute permission={Permission.SOLICITUDES_READ}>
            <SolicitudesListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'keycloak-orphans',
        element: (
          <ProtectedRoute permission={Permission.USERS_READ}>
            <KeycloakOrphansPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'clientes',
        element: (
          <ProtectedRoute permission={Permission.CLIENTES_READ}>
            <ClientesListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'clientes/:id',
        element: (
          <ProtectedRoute permission={Permission.CLIENTES_READ}>
            <ClienteDetailPage />
          </ProtectedRoute>
        ),
      },
      // Tab-per-path Cliente 360 (/clientes/:id/servicios, /facturas, ...).
      // React Router ranks the static `edit` segment above :tab, so
      // /clientes/:id/edit keeps resolving to ClienteEditPage.
      {
        path: 'clientes/:id/:tab',
        element: (
          <ProtectedRoute permission={Permission.CLIENTES_READ}>
            <ClienteDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'clientes/:id/edit',
        element: (
          <ProtectedRoute permission={Permission.CLIENTES_UPDATE}>
            <ClienteEditPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'servicios',
        element: (
          <ProtectedRoute permission={Permission.SERVICIOS_READ}>
            <ServiciosListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'facturas',
        element: (
          <ProtectedRoute permission={Permission.COBROS_READ}>
            <CobrosListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'facturas/generar',
        element: (
          <ProtectedRoute permission={Permission.COBROS_GENERAR}>
            <CobrosGenerarPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'facturas/historial-generacion',
        element: (
          <ProtectedRoute permission={Permission.COBROS_READ}>
            <CobrosGeneracionHistorialPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reportes/corte',
        element: (
          <ProtectedRoute permission={Permission.COBROS_READ}>
            <CorteMoraPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'pagos',
        element: (
          <ProtectedRoute permission={Permission.PAGOS_READ}>
            <PagosListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'excepciones',
        element: (
          <ProtectedRoute permission={Permission.EXCEPCIONES_READ}>
            <ExcepcionesListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'visitas',
        element: (
          <ProtectedRoute permission={Permission.VISITAS_READ}>
            <VisitasListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'comisiones',
        element: (
          <ProtectedRoute permission={Permission.COMISIONES_READ}>
            <ComisionesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'catalogos',
        element: (
          <ProtectedRoute permission={Permission.CATALOGOS_READ}>
            <CatalogosListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'catalogos/category/new',
        element: (
          <ProtectedRoute permission={Permission.CATALOGOS_CREATE}>
            <CatalogCategoryCreatePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'catalogos/category/:category/edit',
        element: (
          <ProtectedRoute permission={Permission.CATALOGOS_UPDATE}>
            <CatalogCategoryEditPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'catalogos/category/:category',
        element: (
          <ProtectedRoute permission={Permission.CATALOGOS_READ}>
            <CatalogosCategoryPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'catalogos/new',
        element: (
          <ProtectedRoute permission={Permission.CATALOGOS_CREATE}>
            <CatalogoCreatePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'catalogos/:id',
        element: (
          <ProtectedRoute permission={Permission.CATALOGOS_READ}>
            <CatalogoDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'catalogos/:id/edit',
        element: (
          <ProtectedRoute permission={Permission.CATALOGOS_UPDATE}>
            <CatalogoEditPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'planes',
        element: (
          <ProtectedRoute permission={Permission.PLANES_READ}>
            <PlanesListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'planes/new',
        element: (
          <ProtectedRoute permission={Permission.PLANES_CREATE}>
            <PlanCreatePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'planes/:id',
        element: (
          <ProtectedRoute permission={Permission.PLANES_READ}>
            <PlanDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'planes/:id/edit',
        element: (
          <ProtectedRoute permission={Permission.PLANES_UPDATE}>
            <PlanEditPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tipos-servicio',
        element: (
          <ProtectedRoute permission={Permission.TIPOS_SERVICIO_READ}>
            <TiposServicioListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tipos-servicio/new',
        element: (
          <ProtectedRoute permission={Permission.TIPOS_SERVICIO_CREATE}>
            <TipoServicioCreatePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tipos-servicio/:id',
        element: (
          <ProtectedRoute permission={Permission.TIPOS_SERVICIO_READ}>
            <TipoServicioDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tipos-servicio/:id/edit',
        element: (
          <ProtectedRoute permission={Permission.TIPOS_SERVICIO_UPDATE}>
            <TipoServicioEditPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
